import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Input, DatePicker, Space, Tag, Descriptions, Select, Button, Table } from 'antd';
import { MoreOutlined, SendOutlined, DollarOutlined, FileTextOutlined } from '@ant-design/icons';
import { useAppContext } from '../../libs/contextLib';
import { startCase } from 'lodash';
import dayjs from '../../dayjs';
import config from '../../config';
import DocumentActionsMenu from '../../components/DocumentActionsMenu';
import DocumentHistory from '../../components/DocumentHistory';
import { DATE_STRING } from '../../model/busRoute';

const { TextArea } = Input;

const BookingInvoice = ({ mode, charter, setBookingWith, onAction, preview, mail }) => {
  const { operator } = useAppContext();
  const [logoUrl, setLogoUrl] = useState(null);
  const invoiceDetails = charter.invoiceDetails || {};

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  useEffect(() => {
    if (operator) {
      setLogoUrl(`https://${config.s3.BUCKET}.s3.ap-southeast-2.amazonaws.com/public/${operator?.operatorId}/logo.png?${Date.now()}`);
    }
  }, [operator]);

  const subtotal = charter.calculateSubTotal();
  const afterDiscount = charter.calculateTotalAfterDiscount();
  const gst = charter.calculateGST();
  const total = charter.calculateTotal();

  const payments = charter.receiptDetails?.payments || [];
  const totalPaid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const balance = total - totalPaid;

  return (
    //< 
      // title={<DocumentActionsMenu 
      //   mode={mode}
      //   charter={charter} 
      //   onAction={onAction}
      //   currentView="invoice"
      //   preview={preview}
      //   mail={mail}
      //   type="invoice"
      // />} 
      // bordered={false} 
      // className="card-main"
    //>
    <>
      <Row gutter={[30, 30]}>
        <Col span={7} className="d-flex flex-column">
          <label>Invoice Details</label>
          {mode.edit ? (
            <Input
              value={invoiceDetails.invoiceNumber}
              onChange={(e) => setBookingWith('invoiceDetails', { ...invoiceDetails, invoiceNumber: e.target.value })}
            />
          ) : (
            <strong>{invoiceDetails.invoiceNumber || '-'}</strong>
          )}

          <div style={{ height: 30 }} />

          <label>Purchase Order Number</label>
          {mode.edit ? (
            <Input
              value={invoiceDetails.purchaseOrderNumber}
              onChange={(e) => setBookingWith('invoiceDetails', { ...invoiceDetails, purchaseOrderNumber: e.target.value })}
            />
          ) : (
            <strong>{invoiceDetails.purchaseOrderNumber || '-'}</strong>
          )}
        </Col>
        <Col span={6} className="d-flex flex-column">
          <label>Invoice Date</label>
          {mode.edit ? (
            <DatePicker
              value={invoiceDetails.invoiceDate ? dayjs(invoiceDetails.invoiceDate) : dayjs()}
              format={{
                format: DATE_STRING,
                type: "mask",
              }}
              onChange={(date) => setBookingWith('invoiceDetails', { ...invoiceDetails, invoiceDate: date })}
            />
          ) : (
            <strong>{invoiceDetails.invoiceDate ? dayjs(invoiceDetails.invoiceDate).format('DD/MM/YYYY') : '-'}</strong>
          )}

          <div style={{ height: 30 }} />

          <label>Payment Terms</label>
          {mode.edit ? (
            <Select value={invoiceDetails.paymentTerms || '7 days'}
              onChange={(value) => setBookingWith('invoiceDetails', { ...invoiceDetails, paymentTerms: value })}
              options={[
                { value: '7 days', label: '7 days' },
                { value: '14 days', label: '14 days' },
                { value: '30 days', label: '30 days' },
                { value: '60 days', label: '60 days' }
              ]}
            />
          ) : (
            <strong>{invoiceDetails.paymentTerms || '-'}</strong>
          )}
        </Col>
        <Col span={6} className="d-flex flex-column">
          <label>Due Date</label>
          {mode.edit ? (
            <DatePicker
              value={invoiceDetails.dueDate ? dayjs(invoiceDetails.dueDate) : dayjs().add(7, 'd')}
              format={{
                format: DATE_STRING,
                type: "mask",
              }}
              onChange={(date) => setBookingWith('invoiceDetails', { ...invoiceDetails, dueDate: date })}
            />
          ) : (
            <strong>{invoiceDetails.dueDate ? dayjs(invoiceDetails.dueDate).format('DD/MM/YYYY') : '-'}</strong>
          )}
        </Col>
        <Col xs={5} className="d-flex align-items-center justify-content-end">
          {logoUrl &&
            <img
              width={150}
              src={logoUrl}
              onError={(e) => {
                e.nativeEvent.target.style.width = 0;
                e.nativeEvent.target.style.height = 0;
              }}
            />}
        </Col>
      </Row>
      <hr className="row-separator full-width" style={{ marginTop: 30 }} />
      <div className="invoice-line-items">
        <Row className="card-row-header align-items-center">
          <Col xs={6}>
            <span>Name</span>
          </Col>
          <Col xs={10} className="text-left">
            <span>Description</span>
          </Col>
          <Col xs={2}>
            <span>Price</span>
          </Col>
          <Col xs={2}>
            <span>Qty</span>
          </Col>
          <Col xs={2}>
            <span>Discount</span>
          </Col>
          <Col xs={2} className="text-right">
            <span>Amount</span>
          </Col>
        </Row>
        {charter.quoteLineItems.map((item, index) => (
          <Row key={index} className="card-row-content border-full align-items-center">
            <Col xs={6}>
              <strong>{startCase(item.name)}</strong>
            </Col>
            <Col xs={10} className="text-left" style={{ lineHeight: '16px' }}>
              <span>{item.description}</span>
            </Col>
            <Col xs={2}>
              <span>{formatCurrency(item.unitPrice)}</span>
            </Col>
            <Col xs={2}>
              <span>{item.quantity}</span>
            </Col>
            <Col xs={2}>
              <span>{formatCurrency(item.discount || 0)}</span>
            </Col>
            <Col xs={2} className="text-right">
              <strong>{formatCurrency(item.total)}</strong>
            </Col>
          </Row>
        ))}
      </div>

      <div className="invoice-totals" style={{marginTop: 30}}>
        <Row className="card-row-content border-full align-items-center no-border">
          <Col className="label-col">
            <strong>TOTAL DUE</strong>
          </Col>
          <Col className="value-col text-right">
            <strong style={{ fontSize: "18px" }}>
              {formatCurrency(total)}
            </strong>
          </Col>
        </Row>
        <Row className="card-row-content border-full align-items-center no-border">
          <Col className="label-col">
            <strong>TOTAL PAID</strong>
          </Col>
          <Col className="value-col text-right">
            <strong style={{ fontSize: "15px", color: '#389e0d' }}>
              {formatCurrency(totalPaid)}
            </strong>
          </Col>
        </Row>
        <Row className="card-row-content border-full align-items-center">
          <Col className="label-col">
            <strong style={{ fontSize: "18px" }}>BALANCE DUE</strong>
          </Col>
          <Col className="value-col text-right">
            <strong style={{ fontSize: "18px", color: balance > 0 ? '#ff4d4f' : '#389e0d' }}>
              {formatCurrency(balance)}
            </strong>
          </Col>
        </Row>
      </div>

      {payments.length > 0 && (
        <div style={{ margin: '30px -24px', borderTop: '1px solid #EFEDFD', padding: '30px 24px 0' }}>
          <h3 style={{fontSize: '18px', fontWeight: '600', marginBottom: 20}}>Payment History</h3>
          <Table
            dataSource={payments.map((p, i) => ({ ...p, key: i }))}
            pagination={false}
            size="small"
            columns={[
              { title: 'Date', dataIndex: 'date', key: 'date' },
              { title: 'Method', dataIndex: 'method', key: 'method', render: val => startCase(val) },
              { title: 'Reference', dataIndex: 'reference', key: 'reference' },
              { title: 'Note', dataIndex: 'note', key: 'note' },
              { title: 'Amount', dataIndex: 'amount', key: 'amount', render: val => formatCurrency(val) }
            ]}
            // summary={pageData => {
            //   let runningTotal = 0;
            //   return (
            //     <>
            //       <Table.Summary.Row>
            //         <Table.Summary.Cell index={0}><strong>Total Paid</strong></Table.Summary.Cell>
            //         <Table.Summary.Cell index={2} colSpan={3} />
            //         <Table.Summary.Cell index={1}><strong>{formatCurrency(totalPaid)}</strong></Table.Summary.Cell>
            //       </Table.Summary.Row>
            //       <Table.Summary.Row>
            //         <Table.Summary.Cell index={0}><strong>Outstanding Balance</strong></Table.Summary.Cell>
            //         <Table.Summary.Cell index={2} colSpan={3} />
            //         <Table.Summary.Cell index={1}><strong>{formatCurrency(balance)}</strong></Table.Summary.Cell>
            //       </Table.Summary.Row>
            //     </>
            //   );
            // }}
          />
        </div>
      )}

      {/* <DocumentHistory charter={charter} /> */}
    </>
  );
};

export default BookingInvoice; 